<template>
<div>

  <modal-component v-model="modal.receiptDetails.active">
    <div slot="title">{{ modal.receiptDetails.title }}</div>
    <receipt-details
      v-if="modal.receiptDetails.paymentID"
      :paymentID="modal.receiptDetails.paymentID"
      :creditNoteID="creditNote.id"
    ></receipt-details>
  </modal-component>

  <item-history
    :active.sync="itemHistory.active"
    :product="itemHistory.product"
    :type.sync="itemHistory.type"
    v-if="allowActions"
    @updated-product="itemHistory.product = $event, get()"
  ></item-history>


  <div class="box" v-if="creditNote.id">
    <div class="flex flex-wrap">
      <div class="w-full md:w-1/2">
        <img class="h-10" :src="!config.logoPrintPath || url.resolve(config.URL.API.host, config.logoPrintPath)">
      </div>
      <div class="w-full md:w-1/2 md:text-right">
        <p
          class="font-bold uppercase"
          :class="creditNote.type === 'purchases' ? 'text-red-500' : 'text-green-500'"
          v-if="preview"
        >{{ creditNote.type.slice(0, -1) }}</p>

        <div class="flex items-center justify-end">
          <p class="text-2xl font-bold">
            Credit note
          </p>
          <router-link
            class="btn btn-orange text-xs ml-2"
            :to="'/credit-note/edit/' + creditNote.id"
            v-if="preview && allowEdit && hasPermission('credit-note.edit')"
          >
            <i class="fas fa-edit"></i>
            <span class="ml-1">Edit</span>
          </router-link>
        </div>
      </div>
    </div>

    <div class="flex flex-wrap">
      <div class="w-full md:w-8/12" v-if="creditNote.contact">
        <div class="mt-3 pb-3 border-b border-theme-200 light:border-theme-600">
          <a class="text-link" @click.stop="$router.push($contactPageByType(creditNote.contact, 'details'))">
            <span class="mb-0 text-lg">
              {{ creditNote.contact.first_name }} {{ creditNote.contact.last_name }}
              <template v-if="creditNote.contact.company_name"> ({{ creditNote.contact.company_name }})</template>
              <small>#{{ creditNote.contact.id }}</small>
            </span>
          </a>
          <p v-if="creditNote.invoice.supplier_account">
            {{ creditNote.invoice.supplier_account.display_name }}
          </p>
        </div>

        <div class="flex flex-wrap text-sm mt-2">
          <div class="w-full md:w-1/2">
            <div v-for="(addr, i) in creditNote.contact.addresses" :key="'address-' + i">
              <p class="text-lg font-bold">Address</p>
              <address>
                {{ addr.address_1 }},<br>
                {{ addr.city }},
                <span v-if="addr.state">{{ addr.state }}, </span>
                <span>{{ addr.post_code }}</span>,<br>
                <span>{{ CountryByID(addr.country_id).name }}</span>
              </address>
            </div>
          </div>
          <div class="w-full md:w-1/2">
            <p class="text-lg font-bold">Phone</p>
            <p class="font-bold" v-for="(phone, i) in creditNote.contact.phone_numbers" :key="'phone-' + i">
              <template v-if="phone.countryData = CountryByID(phone.country_id)">
                <span :title="phone.countryData.name">{{ '+' + phone.countryData.calling_code }}</span> {{ phone.number }}
              </template>
            </p>
          </div>
        </div>
      </div>

      <div class="w-full md:w-4/12 flex flex-col">
        <div class="self-end pb-3 border-b border-theme-200 light:border-theme-600 text-sm">
          <table>
            <tbody>
              <tr>
                <td colspan="2" class="font-bold text-lg text-right">
                  <p class="leading-none">
                    #{{ creditNote.id }}
                  </p>
                  <router-link
                    class="text-link text-xs leading-none block"
                    :to="'/invoice/preview/' + creditNote.invoice.id"
                    v-if="hasPermission('invoice.view')"
                  >
                    for invoice #{{ creditNote.invoice.id }}
                  </router-link>
                </td>
              </tr>
              <tr>
                <td>Date:</td>
                <td class="text-right">
                  {{ $moment(creditNote.created_at).format(config.dateFormats.display) }}
                </td>
              </tr>
              <tr>
                <td>Status:</td>
                <td class="text-right">
                  <div
                    class="text-white rounded inline-block leading-tight px-1"
                    :class="isSettled ? 'bg-green-500' : 'bg-red-500'"
                  >
                    {{ isSettled ? 'Settled' : 'Not settled'}}
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="text-sm text-right mt-2">
          <p class="text-base font-bold">{{ config.companyName }}</p>
          <p>{{ config.companyAddress }}, {{ config.companyCity }}</p>
          <p v-if="config.companyState">{{ config.companyState }}, </p>
          <p>{{ config.companyPostCode }}, {{ config.companyCountry }}</p>
          <p>{{ config.companyPhone }}</p>
          <p v-if="config.companyVATNumber"><strong>VAT:</strong> {{ config.companyVATNumber }}</p>
          <p v-if="config.companyIBAN"><strong>IBAN:</strong> {{ config.companyIBAN }}</p>
          <p v-if="config.companyAccountNumber"><strong>Account No:</strong> {{ config.companyAccountNumber }}</p>
        </div>
      </div>
    </div>

    <div class="w-1/3 mt-4" v-if="creditNote.type === 'purchases'">
      <label for="external-id">External ID</label>
      <div class="flex">
        <div class="flex-grow">
          <input name="external_id" type="text" class="form-control" v-model="creditNote.external_id">
        </div>
        <button
          type="button"
          class="btn btn-blue ml-2"
          @click="save"
          v-if="creditNote.external_id !== initial.external_id"
        >
          <i class="fas fa-check"></i>
          <span class="ml-2">Save</span>
        </button>
      </div>
    </div>


    <table-editor
      v-if="creditNote && creditNote.items && creditNote.contact.id"
      :allow-actions="allowActions"
      :items="creditNote.items"
      :transaction-type="creditNote.type"
      :contactID="creditNote.contact.id"
      :contact-type="contactType"
      :preSelectedProductID="preSelectedProductID"
      :supplier-account="creditNote.invoice.supplier_account || {}"
      v-model="editor.item.active"
      document-type="credit-note"
      :invoiceID="creditNote.invoice.id"
      @item-deleted="save"
      @item-added="save"
      @openItemHistory="openItemHistory"
    />

    <div class="my-4" v-if="!editor.item.active && allowActions">
      <button type="button" class="btn py-2 px-3 btn-purple" @click="editor.item.active = true">
        <i class="fas fa-plus"></i>
        <span class="ml-3">Add item</span>
      </button>
    </div>


    <div class="flex flex-wrap mt-8">
      <div class="w-full md:w-1/2">
        <template v-if="!isPaidFully && !inModal && !isSettled && hasAllPermissions(['credit-note.edit', 'payment.create'])">
          <p class="text-xl font-bold">Payments</p>

          <div class="border border-theme-200 light:border-theme-600 py-6 px-4">
            <template v-if="!editor.payment.active">
              <button type="button" class="btn btn-green font-bold py-2 px-4" @click="editor.payment.active = true">
                <i class="fas fa-receipt"></i>
                <span class="ml-3">Create payment</span>
              </button>
            </template>

            <payment-editor
              v-model="editor.payment.data"
              :payment-type="editor.payment.type"
              :active.sync="editor.payment.active"
              :close-button="true"
              :amount="this.total.sub - this.total.discount + this.total.tax - +this.total.payments"
              @save="savePayment"
            ></payment-editor>
          </div>
        </template>
      </div>
      <div class="w-full md:w-1/2 flex flex-col">
        <div class="self-end text-lg">
          <table class="w-full">
            <tbody>
              <tr>
                <td>Sub total:</td>
                <td class="font-bold text-right">{{ $formatMoney(total.sub) }}</td>
              </tr>
              <tr v-if="total.discount">
                <td>Discount:</td>
                <td class="font-bold text-right">{{ $formatMoney(total.discount) }}</td>
              </tr>
              <tr v-if="total.tax">
                <td>Tax:</td>
                <td class="font-bold text-right">{{ $formatMoney(total.tax) }}</td>
              </tr>
              <tr class="border-t border-theme-200 light:border-theme-600 text-xl">
                <td class="pt-4 pr-4">Grant total:</td>
                <td class="pt-4 font-bold text-right">{{ $formatMoney(total.sub - total.discount + total.tax) }}</td>
              </tr>
            </tbody>
          </table>

          <div v-if="editor.payment.active || creditNote.payments.length" class="mt-8 px-2 py-1 border border-theme-200 light:border-theme-600">
            <p class="text-xl font-bold text-center border-blyat mb-2">Payments</p>

            <div
              class="bg-theme-600 hover:bg-theme-800 cursor-pointer mb-1"
              v-for="(payment, i) in creditNote.payments" :key="'total-payments-' + i"
              @click="openReceipt(payment.id)"
            >
              <div class="flex flex-wrap items-center px-2 py-2 text-sm">
                <div class="w-1/2 px-2">
                  <p>{{ payment.method }} ({{ payment.status }})</p>
                  <p class="text-xs whitespace-no-wrap break-all">{{ $moment(payment.created_at).format(config.dateTimeFormats.display) }}</p>
                </div>
                <div class="w-1/2 px-2 text-right">
                  <p class="text-red font-bold" v-if="isReceipt(payment)">GROSS</p>
                  <p class="font-bold">{{ $formatMoney(payment.value) }}</p>
                </div>
              </div>
            </div>

            <div class="flex flex-wrap items-center px-2 py-2 my-1 text-sm border border-theme-200 light:border-theme-600" v-if="editor.payment.active">
              <div class="w-2/3 px-2">
                <p>{{ editor.payment.data.method }} (new)</p>
                <p class="text-xs whitespace-no-wrap break-all" v-if="timeNow">{{ timeNow.format(config.dateTimeFormats.display) }}</p>
              </div>
              <div class="w-1/3 px-2">
                <p class="font-bold">{{ $formatMoney(editor.payment.data.value) }}</p>
              </div>
            </div>

            <div v-if="!creditNote.receipts || !creditNote.receipts.length">
              <div class="flex flex-wrap items-center pt-2 mt-1 text-sm" v-if="creditNote.payments.length > 0">
                <div class="w-1/2 px-2">
                  <p>Payments total:</p>
                </div>
                <div class="w-1/2 px-2">
                  <p class="font-bold text-right">{{ $formatMoney(total.payments + (editor.payment.active ? +editor.payment.data.value : 0)) }}</p>
                </div>
              </div>
              <div class="flex flex-wrap items-center pb-2 mb-1 border-t border-theme-200 light:border-theme-600" v-if="!isPaidFully">
                <div class="w-1/2 px-2">
                  <p>Remaining:</p>
                </div>
                <div class="w-1/2 px-2">
                  <p class="font-bold text-right">{{ $formatMoneyToFixed(this.totalRemaining - (editor.payment.active ? +editor.payment.data.value : 0)) }}</p>
                </div>
              </div>
            </div>

          </div>
        </div>

      </div>
    </div>


    <div class="flex flex-wrap flex-col mt-8">
      <div class="self-end text-right">
        <div
          class="btn px-3 py-2 btn-purple"
          @click="settle()"
          v-if="creditNote.items.length && isPaidFully && !isSettled && !inModal && hasPermission('credit-note.edit')"
        >
          <i class="fas fa-check"></i>
          <span class="ml-2">Settle credit note</span>
        </div>

        <router-link
          :to="'/credit-note/edit/' + creditNote.id"
          class="btn px-3 py-2 btn-orange ml-3"
          v-if="preview && allowEdit && hasPermission('credit-note.edit')">
          <i class="fas fa-edit"></i>
          Edit credit note
        </router-link>

        <div class="btn px-3 py-2 btn-blue ml-3" @click="print">
          <i class="fas fa-print"></i>
          <span class="ml-2">Print</span>
        </div>
      </div>
    </div>

  </div>



</div>
</template>

<script>
import axios from 'axios'
import path from 'path'
import url from 'url'
import {mapActions, mapGetters} from 'vuex'

const ItemHistory = resolve => require(['@/components/pages/credit-notes/ItemHistory'], resolve)
const ReceiptDetails = resolve => require(['@/components/pages/receipts/Details'], resolve)
const PaymentEditor = resolve => require(['@/components/pages/payments/Editor'], resolve)
const TableEditor = resolve => require(['@/components/TableEditor'], resolve)
const ModalComponent = resolve => require(['@/components/Modal'], resolve)

var pdfMake = require('pdfmake/build/pdfmake.js')
var pdfFonts = require('pdfmake/build/vfs_fonts.js')
pdfMake.vfs = pdfFonts.pdfMake.vfs

export default {
  name: 'credit-note-edit',
  props: {
    creditNoteID: {},
    creditNoteType: {
      type: String,
      default: 'purchases',
    },
    contactID: {},
    contactEditable: {
      type: Boolean,
      default: true,
    },
    inModal: Boolean,
    preSelectedProductID: {},
  },
  components: {
    ItemHistory,
    ReceiptDetails,
    PaymentEditor,
    ModalComponent,
    TableEditor,
  },
  data() {
    return {
      url,
      mode: null,
      preview: false,
      creditNote: {},
      editID: null,
      list: {
        customer: [],
        supplier: [],
      },
      editProductID: null,
      editingItemIndex: null,
      contactType: '',
      timeNow: this.$moment(),
      modelSelectRefs: {customer: {}, supplier: {}},
      initial: {
        contact: {},
        external_id: '',
      },
      itemHistory: {
        active: false,
        product: {},
        type: '',
      },
      editor: {
        item: {
          active: false,
        },
        payment: {
          active: false,
          autoValue: '',
          displayValue: '',
          data: {}
        }
      },
      modal: {
        receiptDetails: {
          active: false,
          title: '',
          paymentID: null,
        },
      },
      delays: {
        products: {
          d: null,
          t: 500,
        },
        timeNow: {
          d: null,
          t: 1000,
        },
      }
    }
  },
  watch: {
    'creditNote.items'() {
      this.creditNote.items.map(v => {
        v.total = {
          raw: v.value * v.quantity,
        }
        v.total.discount = v.total.raw * v.discount / 100
        v.total.tax = (v.total.raw - v.total.discount) * v.tax / 100
        v.total.all = v.total.raw - v.total.discount + v.total.tax

        v.priceWarnings = this.comparePrices(v, v.product)
      })
    },
    invoice() {
      this.setMode()
    },
    creditNoteID() {
      this.setMode()
    },
  },
  computed: {
    allowActions() {
      return !this.inModal && !this.preview && this.allowEdit
    },
    allowEdit() {
      // allow editing of an unsettled credit note, only when created in the current month
      return !this.isSettled && this.$moment(this.creditNote.created_at).isSameOrAfter(this.$moment().startOf('month'))
    },
    totalRemaining() {
      return this.total.sub - this.total.discount + this.total.tax - this.total.payments
    },
    isPaidFully() {
      return Math.round(this.totalRemaining * 100) / 100 <= 0
    },
    isSettled() {
      return !!this.creditNote.settlement_date
    },
    total() {
      if(!this.creditNote.items) {
        return {}
      }

      const total = this.creditNote.items.map(e => e.total)
      return {
        sub: total.map(e => e.raw).reduce((a, b) => a + b, 0),
        discount: total.map(e => e.discount).reduce((a, b) => a + b, 0),
        tax: total.map(e => e.tax).reduce((a, b) => a + b, 0),
        payments: this.creditNote.payments.map(e => e.value).reduce((a, b) => a + b, 0)
      }
    },
    itemTotals() {
      const total = {
        raw: this.editor.item.data.value * this.editor.item.data.quantity,
      }
      total.discount = total.raw * this.editor.item.data.discount / 100
      total.tax = total.raw * this.editor.item.data.tax / 100
      total.all = total.raw - total.discount + total.tax

      return total
    },
    itemPriceWarnings() {
      let item = this.editor.item.data
      let product = this.editor.item.data.product

      return this.comparePrices(item, product)
    },
    ...mapGetters({
      CountryByID: 'countries/countryByID',
      config: 'config/config',
      hasPermission: 'user/hasPermission',
      hasAllPermissions: 'user/hasAllPermissions'
    })
  },
  mounted() {
    this.setMode()
    this.delays.timeNow.d = setInterval(() => {
      this.timeNow = this.$moment()
    }, this.delays.timeNow.t)
    // get financial accounts
    this['config/getFinancialAccounts']()
  },
  destroyed() {
    clearInterval(this.delays.timeNow.d)
  },
  methods: {
    isReceipt(payment) {
      if (!this.creditNote.receipts || !this.creditNote.receipts.length) {
        return false
      }
      for (let i in this.creditNote.receipts) {
        if (this.creditNote.receipts[i].payment_id === payment.id) {
          return true
        }
      }
      return false
    },
    openReceipt(paymentID) {
      this.modal.receiptDetails.paymentID = paymentID
      this.modal.receiptDetails.active = true
    },
    openItemHistory(product) {
      if (this.itemHistory.product.id === product.id) {
        this.closeItemHistory()
      }
      this.itemHistory.active = true
      this.itemHistory.product = product
      this.itemHistory.type = this.contactType === 'supplier' ? 'purchases' : 'sales'
    },
    closeItemHistory() {
      this.itemHistory.active = false
      this.itemHistory.product = {}
      this.itemHistory.type = ''
    },
    comparePrices(item, product) {
      if (!product) {
        return ''
      }

      const total = {
        raw: item.value * item.quantity,
      }
      total.discount = total.raw * item.discount / 100
      total.tax = total.raw * item.tax / 100
      total.all = total.raw - total.discount + total.tax
      total.all = total.raw - total.discount + total.tax

      if (this.creditNote.type === 'sales') {
        let productSaleTotal = product.sale_price * item.quantity + (product.sale_price * item.quantity * product.sale_tax / 100)
        if (item.value < product.sale_price) {
          return 'Product\'s sale price is ' + this.$formatMoney(product.sale_price) + (total.all < productSaleTotal ? '. You will potentially lose ' + this.$formatMoney(productSaleTotal - total.all) : '') + '.'
        }
        if (total.all < productSaleTotal) {
          return 'Products total sale price plus tax is ' + this.$formatMoney(productSaleTotal) + '. You will potentially lose ' + this.$formatMoney(productSaleTotal - total.all) + '.'
        }
      } else if (this.creditNote.type === 'purchases') {
        if (item.value > product.purchase_price) {
          return 'Product\'s purchase price is ' + this.$formatMoney(product.purchase_price) + ' (' + this.$formatMoney(item.value - product.purchase_price) + ' increase).'
        }
      }

      return ''
    },
    editProduct() {
      this.$refs.editorModelSelect.edit()
    },
    selectedProduct(product) {
      this.editor.item.data.value = this.creditNote.type === 'sales'? product.sale_price : product.purchase_price
      this.editor.item.data.tax = this.creditNote.type === 'sales'? product.sale_tax: product.purchase_tax
    },
    removeItem(index) {
      this.$swal.fire({
        title: 'Delete item',
        text: 'Are you sure?',
        type: 'question',
        showCancelButton: true,
      })
        .then((result) => {
          if (result.value) {
            this.creditNote.items.splice(index, 1)
            this.save()
          }
        })
    },
    savePayment() {
      this.creditNote.payments.push(Object.assign({}, this.editor.payment.data))
      this.editor.payment.active = false

      // ask to complete
      if (this.isPaidFully) {
        // ask to set settlement date
        this.settle('Credit note paid fully, set as settled?')
      } else {
        this.save()
      }
    },
    setContactType(type) {
      if (this.contactType === type) {
        return
      }

      this.creditNote.contact = {}
      this.list.customer = []
      this.list.supplier = []
      this.contactType = type

      if (this.contactType === this.initial.contact.type) {
        this.creditNote.contact = Object.assign({}, this.initial.contact)
        this.list[this.contactType].push({id: this.creditNote.contact.id, display_name: this.creditNote.contact.display_name})
        return
      }

      // search and populate selected list in model-select
      this.$refs[this.contactType + 'ModelSelect'].search('')
    },
    setContact(contact) {
      this.creditNote.contact_id = contact.id
      this.save()
    },
    setMode() {
      const name = this.$route.name.split('.')
      if (name[0] === 'credit-note' && name[1]) {
        this.mode = name[1]
      } else {
        this.mode = 'create'
      }

      if (this.creditNoteID) {
        this.editID = this.creditNoteID
        this.mode = 'edit'
      } else if (this.$route.params.creditNoteID) {
        this.editID = this.$route.params.creditNoteID
      }

      if (this.mode === 'create') {
        this.creditNote = this.emptyCreditNote()
      } else {
        // get creditNote
        if (this.$route.name === 'creditNote.preview') {
          this.preview = true
        }
        this.get()
      }
    },
    emptyCreditNote() {
      return {
        contact_id: this.contactID,
        invoice_id: this.creditNote.invoice ? this.creditNote.invoice.id : null,
        created_at: this.$moment().format(this.config.dateTimeFormats.api),
        type: this.creditNoteType,
        note: null,
        settlement_date: null,
        items: [],
        payments: [],
      }
    },
    settle(message) {
      this.$swal.fire({
        title: 'Settle credit-note',
        text: message || '',
        type: 'question',
        confirmButtonText: 'Yes',
        showCancelButton: true,
        cancelButtonText: 'No',
      })
        .then((result) => {
          if (result.value) {
            this.creditNote.settlement_date = this.$moment().format(this.config.dateTimeFormats.api)
            this.save()
              .then(() => {
                this.$emit('credit-note-settled')
              })
          }
        })
    },
    get() {
      return new Promise((resolve, reject) => {
        axios.get(path.join('credit-notes', String(this.editID)))
          .then((response) => {
            const creditNote = response.data
            this.creditNote = Object.assign({}, creditNote)
            this.contactType = this.creditNote.contact.type
            this.creditNote.contact.display_name = '#' + this.creditNote.contact.id + ' ' + this.creditNote.contact.first_name + ' ' + this.creditNote.contact.last_name + (this.creditNote.contact.company_name ? ' (' + this.creditNote.contact.company_name + ')' : '')

            this.initial.contact = Object.assign({}, this.creditNote.contact)
            this.initial.external_id = this.creditNote.external_id

            // set payment type (editor)
            // this is the opposite of invoices. purchases means we returned items to supplier therefore have an increase in income
            this.editor.payment.type = this.creditNote.type === 'purchases' ? 'income' : 'expense'

            resolve(creditNote)
          })
          .catch((error) => reject(error))
      })
    },
    save(callback) {
      if (callback) {
        callback(this.save())
        return
      }
      return new Promise((resolve, reject) => {
        if (!this.creditNote.id) {
          axios.post('credit-notes', this.creditNote)
            .then((response) => {
              this.$router.push('/credit-note/edit/' + response.data.id)
              this['config/getFinancialAccounts']()
              this.$emit('created')
              resolve()
            })
            .catch((error) => reject(error))
        } else {
          axios.put('credit-notes/' + this.editID, this.creditNote)
            .then(() => {
              this.get()
              this['config/getFinancialAccounts']()
              this.$emit('updated')
              resolve()
            })
            .catch((error) => reject(error))
        }
      })
    },
    print() {
      // we could store logodata in data()
      // however loading the logo on each print request ensures latest logo usage
      this.creditNoteLogoData()
        .then((data) => {
          this._print(data)
        })
    },
    _print(creditNoteLogoData) {
      this.editor.item.active = false

      let rows = []
      rows.push([
        {text: 'Product', bold: true},
        {text: 'Description', bold: true},
        {text: 'Quantity', bold: true},
        {text: 'Value', bold: true},
        {text: 'Discount', bold: true},
        {text: 'Tax', bold: true},
        {text: 'Total', bold: true},
      ])
      for (let i in this.creditNote.items) {
        let item = this.creditNote.items[i]
        rows.push([
          item.product.name,
          item.description,
          item.quantity,
          this.$formatMoney(item.value),
          item.discount + '%',
          item.tax + '%',
          this.$formatMoney(item.total.all) + '\n' +
          (item.total.discount ? 'Discount: ' + this.$formatMoney(item.total.discount) + '\n' : '') +
          (item.total.tax ? 'Tax: ' + this.$formatMoney(item.total.tax) + '\n' : ''),
        ])
      }

      // let payments = []
      // for (let i in this.creditNote.payments) {
      //   let payment = this.creditNote.payments[i]
      //   payments.push([
      //     [
      //       {text: payment.method + ' (' + payment.status + ')'},
      //       {text: this.$moment(payment.created_at).format(this.config.dateTimeFormats.display), fontSize: 7},
      //     ],
      //     {text: this.$formatMoney(payment.value), bold: true},
      //   ])
      // }
      // payments.push([
      //   {text: 'Remaining:', bold: true, fontSize: 11},
      //   {text: this.$formatMoney((this.total.sub - this.total.discount + this.total.tax - this.total.payments).toFixed(2)), bold: true, fontSize: 11},
      // ])

      let addresses = {width: '*', text: ''}
      let addressList = []
      for (let i in this.creditNote.contact.addresses) {
        let addr = this.creditNote.contact.addresses[i]
        addressList.push(addr.address_1 + ',\n' + addr.city + ', ' + addr.state + ', ' + addr.post_code + ',\n' + this.CountryByID(addr.country_id).name)
      }
      addresses.text = addressList.join('\n')

      let phoneNumbers = {width: '*', text: ''}
      let phoneNumberList = []
      for (let i in this.creditNote.contact.phone_numbers) {
        let phone = this.creditNote.contact.phone_numbers[i]
        phoneNumberList.push('+' + phone.countryData.calling_code + ' ' + phone.number)
      }
      phoneNumbers.text = phoneNumberList.join('\n')

      let companyVATText = [
        { text: '\nVAT: ', fontSize: 9, bold: true },
        { text: this.config.companyVATNumber, fontSize: 9 },
      ]

      let companyIBANText = [
        { text: '\nIBAN: ', fontSize: 9, bold: true },
        { text: this.config.companyIBAN, fontSize: 9 },
      ]

      let companyAccountNumberText = [
        { text: '\nAccount No: ', fontSize: 9, bold: true },
        { text: this.config.companyAccountNumber, fontSize: 9 },
      ]

      var docDefinition = {
        content: [
          {
            columns: [
              {
                width: 90,
                image: creditNoteLogoData,
              },
              {
                width: '*',
                text: '',
              },
              {
                width: '25%',
                text: 'Credit note',
                fontSize: 20,
                bold: true,
                alignment: 'right'
              }
            ],
          },
          {
            columns: [
              {
                width: '*',
                text: '',
              },
              {
                width: '25%',
                text: [
                  '#' + this.creditNote.id + '\n',
                  'Invoice: #' + this.creditNote.invoice.id + '\n',
                  'Date: ' + this.$moment(this.creditNote.created_at).format(this.config.dateFormats.display),
                  '\nStatus: ', {text: (this.isSettled ? 'Paid' : 'Unpaid'), bold: true}
                ],
                alignment: 'right'
              }
            ],
            margin: [ 0, 0, 0, 40 ],
          },
          {
            columns: [
              {
                text: [
                  {text: this.creditNote.contact.first_name + ' ' + this.creditNote.contact.last_name + (this.creditNote.contact.company_name ? ' ' + this.creditNote.contact.company_name : ''), bold: true, fontSize: 12},
                  (this.creditNote.invoice && this.creditNote.invoice.job && this.creditNote.invoice.job.vehicle ? '\n' + this.creditNote.invoice.job.vehicle.make + ' ' + this.creditNote.invoice.job.vehicle.model : ''),
                  (this.creditNote.invoice && this.creditNote.invoice.supplier_account ? '\n' + '#' + this.creditNote.invoice.supplier_account.id + ' ' + this.creditNote.invoice.supplier_account.name + ' ' + this.creditNote.invoice.supplier_account.code : ''),
                ]
              },
              {
                width: '*',
                text: '',
              },
            ],
          },
          {
            canvas: [ { type: 'line', x1: 0, y1: 0, x2: 515 - 25*515/100, y2: 0, lineWidth: 1 } ],
            margin: [ 0, 5, 0, 5 ]
          },
          {
            columns: [
              addresses,
              phoneNumbers,
              {
                width: '30%',
                text: [
                  { text: this.config.companyName + '\n', bold: true, fontSize: 10 },
                  { text: this.config.companyAddress + ' ' + this.config.companyCity + '\n', fontSize: 9 },
                  { text: (this.config.companyState ? this.config.companyState + ', ' : '') + ' ' + this.config.companyPostCode + ' ' + this.config.companyCountry + '\n', fontSize: 9 },
                  { text: this.config.companyPhone, fontSize: 9 },
                  ...this.config.companyVATNumber ? companyVATText : [],
                  ...this.config.companyIBAN ? companyIBANText : [],
                  ...this.config.companyAccountNumber ? companyAccountNumberText : [],
                ],
                alignment: 'right'
              }
            ],
            columnGap: 10
          },
          {
            canvas: [ { type: 'line', x1: 0, y1: 0, x2: 515, y2: 0, lineWidth: 1 } ],
            margin: [ 0, 15, 0, 25 ]
          },
          {
            layout: 'lightHorizontalLines', // optional
            table: {
              // headers are automatically repeated if the table spans over multiple pages
              // you can declare how many rows should be treated as headers
              headerRows: 1,
              widths: ['*', 'auto', 'auto', 50, 50, 50, 100],
              // widths: [ 'auto', '*', 'auto', 100, '*' ],
              body: [
                ...rows,
              ]
            },
            // left, top, right, bottom
            margin: [ 0, 20, 0, 20 ],
          },
          {
            columns: [
              {
                width: '*',
                text: ''
              },
              {
                width: 'auto',
                layout: 'noBorders',
                table: {
                  headerRows: 0,
                  widths: ['auto', 'auto'],
                  body: [
                    ['Sub total:', this.$formatMoney(this.total.sub)],
                    ['Discount:', this.$formatMoney(this.total.discount)],
                    ['Tax:', this.$formatMoney(this.total.tax)],
                    [
                      {},
                      {canvas: [{ type: 'line', x1: 0, y1: 5, x2: 50, y2: 5, lineWidth: 1 }]},
                    ],
                    [
                      {text: 'Grant total: ', bold: true, fontSize: 11},
                      {text: this.$formatMoney(this.total.sub - this.total.discount + this.total.tax), bold: true, fontSize: 11},
                    ],
                  ],
                },
              }
            ],
            margin: [ 0, 0, 0, 20 ]
          },
          {
            columns: [
              {
                width: '35%',
                layout: {
                  hLineWidth(i, node) {
                    return (i === 1 || i === node.table.widths.length) ? 1 : 0
                  },
                  vLineWidth() {
                    return 0
                  },
                  hLineStyle: function () {
                    return {dash: {length: 5, space: 3}}
                  },
                  paddingLeft() { return 0 },
                  paddingRight() { return 10 },
                  paddingTop() { return 10 },
                  paddingBottom() { return 30 },
                },
                table: {
                  headerRows: 0,
                  widths: ['*'],
                  body: [
                    ['Issuer\'s signature'],
                  ],
                }
              },
              {
                width: '*',
                text: ''
              },
              {
                width: '35%',
                layout: {
                  hLineWidth(i, node) {
                    return (i === 1 || i === node.table.widths.length) ? 1 : 0
                  },
                  vLineWidth() {
                    return 0
                  },
                  hLineStyle: function () {
                    return {dash: {length: 5, space: 3}}
                  },
                  paddingLeft() { return 0 },
                  paddingRight() { return 10 },
                  paddingTop() { return 10 },
                  paddingBottom() { return 30 },
                },
                table: {
                  headerRows: 0,
                  widths: ['*'],
                  body: [
                    ['Receiver\'s signature'],
                  ],
                }
              },
            ],
          },
        ],
        footer(currentPage, pageCount) {
          if (pageCount === currentPage) {
            return {
              text: 'Thank you for doing business with us',
              alignment: 'center',
            }
          }
        },
        pageSize: 'A4',
        defaultStyle: {
          fontSize: 9,
          // bold: true
        }
      }

      pdfMake.createPdf(docDefinition).open()
    },
    creditNoteLogoData() {
      return axios.get(this.config.logoPrintPath + '?_', {
        responseType: 'arraybuffer'
      })
        .then(response => {
          let image = btoa(
            new Uint8Array(response.data)
              .reduce((data, byte) => data + String.fromCharCode(byte), '')
          )
          return `data:${response.headers['content-type'].toLowerCase()};base64,${image}`
        })
    },
    ...mapActions([
      'config/getFinancialAccounts'
    ])
  }
}
</script>
